import { useState, useRef, useCallback, useEffect } from 'react';
import { computeZoomTransform, clampToContainer, getTouchDistance, getTouchMidpoint } from '../util/zoom';

/**
 * useDigitalZoom — hook for click-drag box zoom and drag-to-pan.
 *
 * Not zoomed: click-drag draws a selection box, release zooms in.
 * Zoomed: click-drag pans the view. Click (no drag) or right-click resets.
 * Mobile: pinch-to-zoom. Single tap when zoomed resets.
 *
 * @param {Object} options
 * @param {boolean} options.isEview - true for eView green border, false for dView blue
 * @param {number} [options.maxScale=8] - Maximum zoom level
 */
export default function useDigitalZoom({ isEview = false, maxScale = 8 } = {}) {
  // Zoom: { scale, originX, originY, panX, panY }
  const [zoomState, setZoomState] = useState(null);
  const [selectionBox, setSelectionBox] = useState(null);
  const [isPanning, setIsPanning] = useState(false);

  const isDragging = useRef(false);
  const dragStart = useRef(null);
  const justDragged = useRef(false);
  const panStart = useRef(null); // { mouseX, mouseY, panX, panY } for drag-to-pan
  const pinchStart = useRef(null);
  const containerRef = useRef(null);

  const getContainerSize = useCallback(() => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    return { width: rect.width, height: rect.height };
  }, []);

  const getRelativePos = useCallback((clientX, clientY) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const resetZoom = useCallback(() => {
    setZoomState(null);
    setSelectionBox(null);
    setIsPanning(false);
  }, []);

  // Desktop "Reset digital zoom" gear action (#86bb5dx62): a broadcast resets every tile's zoom.
  useEffect(() => {
    const onResetAll = () => resetZoom();
    window.addEventListener('dview-reset-all-zoom', onResetAll);
    return () => window.removeEventListener('dview-reset-all-zoom', onResetAll);
  }, [resetZoom]);

  // Clamp total translate so content edges don't leave the viewport
  // tx/ty are the full translate values (including initial offset + pan)
  const clampTranslate = useCallback((tx, ty, scale) => {
    const size = getContainerSize();
    if (!size) return { tx, ty };
    const w = size.width;
    const h = size.height;
    // Content left edge at screen: tx (since point 0 maps to 0*scale + tx = tx)
    // Content right edge at screen: w*scale + tx
    // Clamp: tx <= 0 (no gap on left), w*scale + tx >= w (no gap on right)
    const clampedTx = Math.min(0, Math.max(w - w * scale, tx));
    const clampedTy = Math.min(0, Math.max(h - h * scale, ty));
    return { tx: clampedTx, ty: clampedTy };
  }, [getContainerSize]);

  // --- Mouse handlers ---

  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();

    if (zoomState) {
      // Start panning
      panStart.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        tx: zoomState.tx,
        ty: zoomState.ty
      };
      setIsPanning(true);
      return;
    }

    // Start selection box for zoom
    const pos = getRelativePos(e.clientX, e.clientY);
    const size = getContainerSize();
    if (!size) return;
    const clamped = clampToContainer(pos.x, pos.y, size);
    isDragging.current = true;
    dragStart.current = clamped;
    setSelectionBox({ x1: clamped.x, y1: clamped.y, x2: clamped.x, y2: clamped.y });
  }, [zoomState, getRelativePos, getContainerSize]);

  const onMouseMove = useCallback((e) => {
    // Panning while zoomed
    if (panStart.current && zoomState) {
      const dx = e.clientX - panStart.current.mouseX;
      const dy = e.clientY - panStart.current.mouseY;
      const newTx = panStart.current.tx + dx;
      const newTy = panStart.current.ty + dy;
      const clamped = clampTranslate(newTx, newTy, zoomState.scale);
      setZoomState(prev => ({ ...prev, tx: clamped.tx, ty: clamped.ty }));
      return;
    }

    // Drawing selection box
    if (!isDragging.current || !dragStart.current) return;
    const pos = getRelativePos(e.clientX, e.clientY);
    const size = getContainerSize();
    if (!size) return;
    const clamped = clampToContainer(pos.x, pos.y, size);
    setSelectionBox({
      x1: dragStart.current.x,
      y1: dragStart.current.y,
      x2: clamped.x,
      y2: clamped.y
    });
  }, [zoomState, getRelativePos, getContainerSize, clampTranslate]);

  const onMouseUp = useCallback((e) => {
    // End panning
    if (panStart.current) {
      const didPan = Math.abs(e.clientX - panStart.current.mouseX) + Math.abs(e.clientY - panStart.current.mouseY) > 5;
      panStart.current = null;
      setIsPanning(false);
      if (didPan) {
        justDragged.current = true;
        setTimeout(() => { justDragged.current = false; }, 50);
      }
      return;
    }

    // End selection box zoom
    if (!isDragging.current || !dragStart.current) return;
    isDragging.current = false;

    const pos = getRelativePos(e.clientX, e.clientY);
    const size = getContainerSize();
    setSelectionBox(null);
    if (!size) return;

    const clamped = clampToContainer(pos.x, pos.y, size);
    const dragDist = Math.abs(clamped.x - dragStart.current.x) + Math.abs(clamped.y - dragStart.current.y);

    // Minimum deliberate-drag distance (px, Manhattan from the mousedown start) before a zoom-select
    // activates — a click or double-click with a few px of pointer drift must NOT trip the digital zoom
    // (David 2026-08-28: "the drag needs a minimum size to stop crap like this"). Below it, the gesture
    // is treated as a plain click, so double-click-to-single-view still works cleanly.
    const MIN_SELECT_DRAG = 16;

    const selection = {
      x1: dragStart.current.x,
      y1: dragStart.current.y,
      x2: clamped.x,
      y2: clamped.y
    };

    const transform = dragDist >= MIN_SELECT_DRAG ? computeZoomTransform(selection, size, maxScale) : null;
    // Never enter zoom for a near-1x result — that leaves the cell stuck in "zoomed" mode (grab cursor,
    // pan handlers on, can't interact) at no visible zoom (David 2026-08-28). Only a real magnification zooms.
    if (transform && transform.scale >= 1.15) {
      // tx/ty position the selection's top-left at the viewport's top-left
      const tx = -transform.left * transform.scale;
      const ty = -transform.top * transform.scale;
      const clamped = clampTranslate(tx, ty, transform.scale);
      setZoomState({ scale: transform.scale, tx: clamped.tx, ty: clamped.ty });
    }

    if (dragDist >= MIN_SELECT_DRAG) {
      justDragged.current = true;
      setTimeout(() => { justDragged.current = false; }, 50);
    }
    dragStart.current = null;
  }, [getRelativePos, getContainerSize, clampTranslate, maxScale]);

  const onClick = useCallback((e) => {
    if (justDragged.current) {
      e.stopPropagation();
      return;
    }
    if (zoomState) {
      e.stopPropagation();
      resetZoom();
    }
  }, [zoomState, resetZoom]);

  const onContextMenu = useCallback((e) => {
    if (zoomState) {
      e.preventDefault();
      e.stopPropagation();
      resetZoom();
    }
  }, [zoomState, resetZoom]);

  // --- Touch handlers ---

  const onTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = getTouchDistance(e.touches[0], e.touches[1]);
      pinchStart.current = { distance: dist };
    } else if (e.touches.length === 1 && zoomState) {
      // Single finger pan when zoomed
      panStart.current = {
        mouseX: e.touches[0].clientX,
        mouseY: e.touches[0].clientY,
        tx: zoomState.tx,
        ty: zoomState.ty
      };
    }
  }, [zoomState]);

  const onTouchMove = useCallback((e) => {
    // Pinch zoom
    if (e.touches.length === 2 && pinchStart.current && !zoomState) {
      e.preventDefault();
      const dist = getTouchDistance(e.touches[0], e.touches[1]);
      const size = getContainerSize();
      if (!size || !pinchStart.current) return;
      const scale = Math.min(dist / pinchStart.current.distance, maxScale);
      if (scale < 1.2) return;
      const mid = getTouchMidpoint(e.touches[0], e.touches[1]);
      const pos = getRelativePos(mid.x, mid.y);
      const s = Math.round(scale * 100) / 100;
      const tx = -pos.x * s + pos.x;
      const ty = -pos.y * s + pos.y;
      setZoomState({ scale: s, tx: Math.round(tx), ty: Math.round(ty) });
    }

    // Single finger pan when zoomed
    if (e.touches.length === 1 && panStart.current && zoomState) {
      e.preventDefault();
      const dx = e.touches[0].clientX - panStart.current.mouseX;
      const dy = e.touches[0].clientY - panStart.current.mouseY;
      const newTx = panStart.current.tx + dx;
      const newTy = panStart.current.ty + dy;
      const clamped = clampTranslate(newTx, newTy, zoomState.scale);
      setZoomState(prev => ({ ...prev, tx: clamped.tx, ty: clamped.ty }));
    }
  }, [zoomState, getContainerSize, getRelativePos, maxScale, clampTranslate]);

  const onTouchEnd = useCallback((e) => {
    if (pinchStart.current) {
      pinchStart.current = null;
      justDragged.current = true;
      setTimeout(() => { justDragged.current = false; }, 300);
      return;
    }
    if (panStart.current) {
      panStart.current = null;
      justDragged.current = true;
      setTimeout(() => { justDragged.current = false; }, 300);
      return;
    }
    // Single tap while zoomed → reset
    if (zoomState && e.changedTouches.length === 1 && e.touches.length === 0) {
      e.preventDefault();
      resetZoom();
    }
  }, [zoomState, resetZoom]);

  // --- Computed styles ---

  const zoomStyle = zoomState ? {
    width: '100%',
    height: '100%',
    transform: `translate(${zoomState.tx}px, ${zoomState.ty}px) scale(${zoomState.scale})`,
    transformOrigin: '0 0',
    transition: isPanning ? 'none' : 'transform 0.2s ease-out'
  } : {
    width: '100%',
    height: '100%',
    transform: 'none',
    transformOrigin: '0 0',
    transition: 'transform 0.15s ease-in'
  };

  const selectionStyle = selectionBox ? {
    position: 'absolute',
    left: Math.min(selectionBox.x1, selectionBox.x2),
    top: Math.min(selectionBox.y1, selectionBox.y2),
    width: Math.abs(selectionBox.x2 - selectionBox.x1),
    height: Math.abs(selectionBox.y2 - selectionBox.y1),
    border: `2px solid var(${isEview ? '--brand-eview' : '--brand-dview'})`,
    backgroundColor: 'transparent',
    pointerEvents: 'none',
    zIndex: 50
  } : null;

  return {
    containerRef,
    zoomState,
    isZoomed: zoomState !== null,
    selectionStyle,
    resetZoom,
    zoomStyle,
    handlers: {
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onClick,
      onContextMenu,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      isPanning
    }
  };
}
